import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "@/services/adminService";
import { houseService } from "@/services/houseService";
import { sellerService } from "@/services/sellerService";
import { walletService } from "@/services/walletService";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import {
  AdminUserDetailPage,
  AdminUserStructuresSection,
  AdminUserActivitiesSection,
  hostDetailToAdmin,
  useAdminUserDetailLabels,
  useAdminUserContactLabels,
} from "@/components/admin/users";
import { resolveCatalogTranslation } from "@/lib/translationResolution";

export default function AdminHostDetailPage() {
  const { t, i18n } = useTranslation("hosts");
  const { hostId } = useParams<{ hostId: string }>();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canListActivities = hasPermission(Permission.HOSTS_READ_ANY);
  const canCreateActivity = hasPermission(Permission.ACTIVITIES_CREATE);

  const detailLabels = useAdminUserDetailLabels({
    roleLabel: t("detail.roleLabel"),
    statRelevant: t("detail.statStructures"),
    loadDetail: t("detail.loadDetail"),
    loadError: t("detail.loadError"),
  });

  const contactLabels = useAdminUserContactLabels("hosts", {
    toastPasswordResetSent: t("detail.toastPasswordResetSent"),
  });

  const {
    data: host,
    isLoading: isLoadingHost,
    error: hostError,
  } = useQuery({
    queryKey: ["admin", "host", hostId],
    queryFn: () => adminService.getHost(hostId!),
    enabled: !!hostId,
  });

  const { data: housesData, isLoading: isLoadingHouses } = useQuery({
    queryKey: ["host-houses", hostId],
    queryFn: () => houseService.listByHost(hostId!),
    enabled: !!hostId,
  });

  const {
    data: activities,
    isLoading: isLoadingActivities,
    error: activitiesError,
  } = useQuery({
    queryKey: ["admin", "host", hostId, "activities"],
    queryFn: () => adminService.getHostActivities(hostId!),
    enabled: !!hostId && canListActivities,
  });

  const houses = housesData ?? [];

  const toggleActiveMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      sellerService.updateSellerProfile(hostId!, { is_active: isActive }),
    onSuccess: () => {
      toast.success(t("detail.toastStatusUpdated"));
      void queryClient.invalidateQueries({ queryKey: ["admin", "host", hostId] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "hosts"] });
    },
    onError: (err) => {
      toastApiError(err, "generic", t("detail.toastStatusError"));
    },
  });

  const updateDisplayNameMutation = useMutation({
    mutationFn: (name: string) =>
      sellerService.updateSellerProfile(hostId!, { name }),
    onSuccess: () => {
      toast.success(detailLabels.toastDisplayNameUpdated);
      void queryClient.invalidateQueries({ queryKey: ["admin", "host", hostId] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "hosts"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err) => {
      toastApiError(err, "generic", detailLabels.toastDisplayNameError);
    },
  });

  const user = useMemo(
    () => (host ? hostDetailToAdmin(host) : undefined),
    [host],
  );

  const activitiesForFilter = activities?.map((a) => ({
    id: a.id,
    name: resolveCatalogTranslation(a.name_translations, i18n.language),
  }));

  if (!hostId) {
    return null;
  }

  return (
    <AdminUserDetailPage
      user={user}
      isLoading={isLoadingHost}
      hasError={!!hostError}
      listPath="/admin/users"
      labels={detailLabels}
      contactLabels={contactLabels}
      canToggleActive={hasPermission(Permission.USERS_ACCOUNT_STATUS_UPDATE)}
      isTogglingActive={toggleActiveMutation.isPending}
      onToggleActive={() => {
        if (user) {
          toggleActiveMutation.mutate(!user.isActive);
        }
      }}
      canEditDisplayName={hasPermission(Permission.USERS_UPDATE_ANY)}
      isUpdatingDisplayName={updateDisplayNameMutation.isPending}
      onUpdateDisplayName={async (name) => {
        await updateDisplayNameMutation.mutateAsync(name);
      }}
      relevantCountOverride={houses.length}
      contactActions={
        hasPermission(Permission.HOSTS_UPDATE_ANY)
          ? {
              onSendPasswordReset: async () => {
                await adminService.sendHostPasswordResetEmail(hostId);
                void queryClient.invalidateQueries({
                  queryKey: ["admin", "host", hostId],
                });
              },
            }
          : undefined
      }
      transactions={{
        fetchTransactions: (params) =>
          walletService.getTransactions(hostId, params),
        fetchStats: (params) => walletService.getStats(hostId, params),
        activities: activitiesForFilter,
      }}
      structuresContent={
        <AdminUserStructuresSection
          hostUserId={hostId}
          houses={houses}
          isLoading={isLoadingHouses}
          title={t("detail.housesTitle")}
          emptyMessage={t("detail.noHouses")}
          canCreateActivity={false}
          canCreateHouse={hasPermission(Permission.HOUSES_CREATE)}
        />
      }
      activitiesContent={
        canListActivities ? (
          <AdminUserActivitiesSection
            ownerUserId={hostId}
            activities={activities}
            isLoading={isLoadingActivities}
            error={!!activitiesError}
            canCreate={canCreateActivity}
            ownerContext="host"
            title={t("detail.activitiesTitle", {
              count: activities?.length ?? 0,
            })}
            loadMessage={t("detail.loadActivities")}
            errorMessage={t("detail.activitiesError")}
            emptyTitle={t("detail.noActivitiesTitle")}
            emptyDescription={t("detail.noActivitiesDesc")}
          />
        ) : undefined
      }
    />
  );
}
